import React from "react";

import {
  List,
  DataTable,
  ColumnsButton,
  TextField,
  ReferenceField,
  Create,
  Edit,
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  EditButton,
  Pagination,
  Button,
  Show,
  TabbedShowLayout,
  Tab,
  TabbedForm,
  useRecordContext,
  TopToolbar,
  CreateButton,
  ShowButton,
  useGetOne,
} from "react-admin";
import { Link } from "react-router-dom";
import { DetailsButton } from "../lib/detailsButton";
import { hasScope, SCOPE } from "../lib/auth";
import { OnlineField } from "../lib/onlineField";
import { DensityField } from "../lib/density";
import BarChartIcon from "@mui/icons-material/BarChart";
import BubblesIcon from "@mui/icons-material/BubbleChart";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { formatSetpoint, formatTemperature } from "../lib/temperature";

import {
  ShowHideToggle,
  SmartTankTemperatureControls,
  SmartTankPumpoverControls,
  SmartTankSpargeControls,
  colours,
} from "../lib/smartTankControl";
import { bwColours } from "../lib/theme";

const SmartTankListActions = () => (
  <TopToolbar>
    {hasScope(SCOPE.ADMIN) ? <CreateButton /> : null}
    <ColumnsButton />
  </TopToolbar>
);

const linkBlue = "#005AA8";
const styles = {
  vcButton: {
    "&:enabled": {
      color: linkBlue,
    },
  },
};

const ViewGrafanaButton = () => {
  const record = useRecordContext();
  if (!record) return null;

  const dashboard = record.name.startsWith("cm13-")
    ? "https://grafana.barrelwise.ca/d/lu2q4dm/smart-tank-details?orgId=8"
    : "https://grafana.barrelwise.ca/d/lu7mf2b/agrovin-integration?orgId=8";

  return (
    <Button
      label="Grafana"
      component={Link}
      target="_blank"
      to={`${dashboard}&var-device_name=${record.name}`}
      sx={styles.vcButton}
    >
      <BarChartIcon />
    </Button>
  );
};

const SmartTankFermentButton = () => {
  const smartTankRecord = useRecordContext();
  const lotVesselID = smartTankRecord?.lotVesselID;

  // Check if a ferment already exists for this lotVesselID.
  // Ferments and lot-vessel associations share the same ID.
  const { data: fermentRecord } = useGetOne(
    "ferments",
    { id: lotVesselID },
    { enabled: !!lotVesselID },
  );

  if (!smartTankRecord) return null;
  if (!lotVesselID) return null;

  // If a ferment already exists, show a link to view it.
  if (fermentRecord)
    return (
      <ShowButton
        label="View Ferment"
        resource="ferments"
        record={{ id: fermentRecord.id }}
        icon={<BubblesIcon />}
        target="_blank"
      />
    );

  // No ferment exists — let MANAGERs create one. The URL includes the
  // customer ID explicitly since react-router-dom's <Link> doesn't respect
  // react-admin's basename.
  if (!hasScope(SCOPE.MANAGER)) return null;

  return (
    <Button
      label="Create Ferment"
      component={Link}
      to={`/c/${smartTankRecord.customerID}/ferments/create?source=${encodeURIComponent(
        JSON.stringify({ vesselID: smartTankRecord.vesselID }),
      )}`}
      sx={{ color: linkBlue }}
    >
      <BubblesIcon />
    </Button>
  );
};

const CurrentTempField = ({ label: _label }) => {
  const record = useRecordContext();
  if (!record) return null;

  const temp_ctl_enable = record.deviceSettings["temp_ctl_enable"];
  const temp_mode = record.deviceSettings["temp_mode"];
  const temp_setpoint = record.deviceSettings["temp_setpoint"];

  let setpoint_display = null;
  if (temp_ctl_enable && temp_mode === 2)
    setpoint_display = (
      <span style={{ color: colours.cooling }}>
        ▼{formatSetpoint(temp_setpoint / 10.0, false)}
      </span>
    );
  else if (temp_ctl_enable && temp_mode === 1)
    setpoint_display = (
      <span style={{ color: colours.heating }}>
        ▲{formatSetpoint(temp_setpoint / 10.0, false)}
      </span>
    );

  if (record.currentTemp === undefined || record.currentTemp === null) {
    return null;
  }
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {formatTemperature(record.currentTemp)} {setpoint_display}
    </span>
  );
};

const CurrentDensityField = ({ label }) => {
  return <DensityField source="currentDensity" label={label} />;
};

const CurrentRedoxField = ({ label: _label }) => {
  const record = useRecordContext();
  if (!record) return null;

  let setpoint_display = null;
  const sparge_enable = record.deviceSettings["sparge_enable"];
  const sparge_mode = record.deviceSettings["sparge_mode"];
  const sparge_setpoint = record.deviceSettings["sparge_setpoint"];

  if (sparge_enable && sparge_mode)
    setpoint_display = (
      <span style={{ color: colours.sparge }}>▲{sparge_setpoint}</span>
    );

  if (!record) return null;

  if (record.currentRedox === undefined || record.currentRedox === null) {
    return null;
  }
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {record.currentRedox + " mV "}
      {setpoint_display}
    </span>
  );
};

const NextPumpoverField = ({ label: _label }) => {
  const record = useRecordContext();
  const pumpov_duration = record?.deviceSettings?.["pumpov_duration"] * 60;
  const pumpov_mode = record?.deviceSettings?.["pumpov_mode"];
  const pumpov_cron = record?.deviceSettings?.["pumpov_cron"];
  const validNextPumpover =
    record &&
    record.nextPumpover !== undefined &&
    record.nextPumpover !== null &&
    pumpov_mode &&
    pumpov_cron &&
    pumpov_duration;

  const [countdown, setCountdown] = React.useState(
    validNextPumpover ? record.nextPumpover : 0,
  );

  React.useEffect(() => {
    if (!validNextPumpover) return;
    setCountdown(record.nextPumpover);
  }, [validNextPumpover, record.nextPumpover]);

  React.useEffect(() => {
    if (!validNextPumpover) return;
    const interval = setInterval(() => {
      setCountdown((prev: number) => {
        if (prev <= -pumpov_duration) {
          clearInterval(interval);
          return -pumpov_duration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [pumpov_duration, validNextPumpover, record.nextPumpover]);

  if (!validNextPumpover) return null;
  else if (countdown < 0 && countdown > -pumpov_duration)
    return <span style={{ color: bwColours.accent.orange }}>running</span>;
  else if (countdown <= -pumpov_duration)
    return <span style={{ color: bwColours.accent.green }}>complete</span>;

  // Format seconds to HH:MM:SS
  const pad = (n: number) => String(n).padStart(2, "0");
  const hours = Math.floor(countdown / 3600);
  const minutes = Math.floor((countdown % 3600) / 60);
  const seconds = countdown % 60;
  const timeString = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

  return <span style={{ whiteSpace: "nowrap" }}>{timeString}</span>;
};

export const SmartTankList = (props) => (
  <List
    {...props}
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    sort={{ field: "vesselName", order: "ASC" }}
    storeKey={false}
    empty={false}
    actions={<SmartTankListActions />}
  >
    <DataTable rowClick={false} bulkActionButtons={false}>
      <DataTable.Col source="vesselName" label="Vessel">
        <AliasOrBarcodeReference
          source="vesselID"
          reference="vessels"
          label="Vessel"
        />
      </DataTable.Col>
      <DataTable.Col source="lotID" label="Current Lot" disableSort>
        <ReferenceField
          source="lotID"
          reference="lots"
          link="show"
          sortable={false}
        >
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="online" label="Online" disableSort>
        <ReferenceField
          source="deviceID"
          reference="devices"
          link={false}
          sortable={false}
        >
          <OnlineField label="Online" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="temp" label="Temp" disableSort>
        <CurrentTempField label="Temp" />
      </DataTable.Col>
      <DataTable.Col source="density" label="Density" disableSort>
        <CurrentDensityField label="Density" />
      </DataTable.Col>
      <DataTable.Col source="redox" label="Redox" disableSort>
        <CurrentRedoxField label="Redox" />
      </DataTable.Col>
      <DataTable.Col source="pumpover" label="Next Pumpover" disableSort>
        <NextPumpoverField label="Next Pumpover" />
      </DataTable.Col>
      {hasScope(SCOPE.TECHNICIAN) ? (
        <DataTable.Col source="deviceID" label="Device" disableSort>
          <ReferenceField
            source="deviceID"
            reference="devices"
            link="edit"
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
        </DataTable.Col>
      ) : null}

      <DataTable.Col disableSort>
        <SmartTankFermentButton />
      </DataTable.Col>
      {hasScope(SCOPE.MANAGER) && (
        <DataTable.Col>
          <DetailsButton />
        </DataTable.Col>
      )}
      {hasScope(SCOPE.ADMIN) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
      {hasScope(SCOPE.ADMIN) ? (
        <DataTable.Col disableSort>
          <ReferenceField source="deviceID" reference="devices" link={false}>
            <ViewGrafanaButton />
          </ReferenceField>
        </DataTable.Col>
      ) : null}
    </DataTable>
  </List>
);

const SmartTankAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span> {record.vesselName}</span>;
};

export const SmartTankControls = () => {
  const hasControlsAccess = hasScope(SCOPE.MANAGER);
  const [showControls, setShowControls] =
    React.useState<boolean>(hasControlsAccess);

  return (
    <>
      <div style={{ height: 30, textAlign: "right" }}>
        {hasControlsAccess ? (
          <ShowHideToggle value={showControls} setValue={setShowControls} />
        ) : null}
      </div>
      <SmartTankTemperatureControls showControls={showControls} />
      <div style={{ marginTop: 30 }} />
      <SmartTankSpargeControls showControls={showControls} />
      <div style={{ marginTop: 30 }} />
      <SmartTankPumpoverControls showControls={showControls} />
      <div style={{ marginTop: 30 }} />
    </>
  );
};

const OverviewTabContents = () => (
  <>
    <ReferenceInput source="vesselID" reference="vessels">
      <AutocompleteInput
        optionText={(rec) => (rec.alias ? rec.alias : rec.barcode)}
        filterToQuery={(searchText) => ({ search: searchText })}
        label="Vessel"
        style={{ minWidth: 300, maxWidth: "100%" }}
      />
    </ReferenceInput>
    <ReferenceInput
      source="deviceID"
      reference="devices"
      filter={{ firmwareName: "cm13-tank-controller" }}
    >
      <AutocompleteInput
        optionText="name"
        filterToQuery={(x) => ({ name: x })}
        label="Device"
        style={{ minWidth: 300, maxWidth: "100%" }}
      />
    </ReferenceInput>
  </>
);

export const SmartTankEdit = () => (
  <Edit redirect="list" title={<SmartTankAppBarTitle />}>
    <TabbedForm>
      <TabbedForm.Tab label="Overview">
        <OverviewTabContents />
      </TabbedForm.Tab>
    </TabbedForm>
  </Edit>
);

export const SmartTankCreate = () => (
  <Create redirect="list">
    <SimpleForm>
      <OverviewTabContents />
    </SimpleForm>
  </Create>
);

export const SmartTankShow = () => (
  <Show title={<SmartTankAppBarTitle />}>
    <TabbedShowLayout>
      <Tab label="Control">
        <SmartTankControls />
      </Tab>
      <Tab label="Attributes">
        <TextField source="id" label="ID" />
        <ReferenceField
          label="Device"
          source="deviceID"
          reference="devices"
          link={hasScope(SCOPE.TECHNICIAN) ? "edit" : false}
        >
          <TextField source="name" />
        </ReferenceField>
        <AliasOrBarcodeReference
          source="vesselID"
          reference="vessels"
          label="Vessel"
        />
        <ReferenceField
          label="Lot"
          source="lotID"
          reference="lots"
          link="show"
          empty="None"
        >
          <TextField source="name" />
        </ReferenceField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);
